import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'subtle';
type Size = 'sm' | 'md' | 'lg' | 'icon';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  children: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-accent text-accent-fg hover:bg-accent-hover ' +
    'active:translate-y-px active:shadow-xs',
  secondary:
    'bg-card text-ink ring-1 ring-inset ring-line shadow-xs ' +
    'hover:bg-surface hover:ring-line-strong active:translate-y-px',
  subtle:
    'bg-accent-soft text-accent-soft-fg hover:brightness-95 active:translate-y-px',
  danger:
    'bg-danger text-accent-fg hover:brightness-110 active:translate-y-px',
  ghost: 'text-muted hover:bg-surface-2 hover:text-ink active:translate-y-px',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-2.5 text-xs gap-1.5',
  md: 'h-9 px-3.5 text-sm gap-2',
  lg: 'h-11 px-5 text-sm gap-2',
  icon: 'h-9 w-9 justify-center',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      // Explicit, because a button inside a form defaults to submit and
      // silently submitting is a classic source of surprise.
      type={rest.type ?? 'button'}
      // A loading button must not accept a second click: that is how duplicate
      // records get created.
      disabled={disabled === true || loading}
      className={clsx(
        'inline-flex select-none items-center justify-center rounded-lg font-medium',
        // Transition the properties that actually change, not `all` — `all`
        // animates layout properties too and produces jitter on hover.
        'transition-[background-color,color,box-shadow,transform,filter] duration-150',
        'ease-[cubic-bezier(0.22,1,0.36,1)]',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
}
